import Banner from '@cpg-its/ads.containers.banner';
import { Hidden } from '@material-ui/core';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { useRoleObject } from '../../../components/useRoles';
import { JuniorGroupPlanAdmin, SeniorGroupPlanAdmin } from '../../../constants/constant-strings';
import { ArcRoutes } from '../../../constants/routes';
import { TerminationProps } from '../../../models/arc-epi-props';
import { EmploymentState } from '../../employment/employment-model';
import { unSetEmploymentState } from '../../employment/employment-slice';
import { getConfirmationGpsType, getTerminationConfirmationContent } from '../employment-termination-helpers';
import { TerminationTypes } from '../models/employment-termination-model';


export function EmploymentTerminationConfirmation() {
	const app = useContext(DemographicsContext);

	const terminationProps = useSelector<TcsmStates, TerminationProps>(state => state.epiProps?.terminationSettingsState?.epiContent);

	const dispatch = useDispatch();
	const location = useLocation();
	const locationState: any = location?.state;
	const mapHome = useBasePath(ArcRoutes.ArcRoot);
	const roles = useRoleObject()?.mlps?.map(a => a.role_name);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const coverageRoute = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Coverage}`);

	const isGroupAdmin = roles?.includes(JuniorGroupPlanAdmin) || roles?.includes(SeniorGroupPlanAdmin);

	const pageContent = getTerminationConfirmationContent(terminationProps, locationState?.terminationType, locationState?.employmentTypes, locationState?.message);
	pageContent.benefitTerminatedTitle = terminationProps?.benefitTerminatedHeader;
	pageContent.benefitTerminatedDescription = (locationState?.terminationType === TerminationTypes.LeaveOfAbsence || locationState?.immediate)
		? terminationProps?.employmentBenefitTerminationMessage
		: terminationProps?.retroExtenBenefitTerminationMessage;
	const gpsType = getConfirmationGpsType(locationState?.terminationType, locationState?.employmentTypes);
	const gpsItem = getGpsType(gpsType, app);

	useEffect(() => {

		// unload employment state on cleanup if for death
		return function cleanup() {
			if (locationState?.terminationType === TerminationTypes.Death) {
				dispatch(unSetEmploymentState());
			}
		};
	}, []);

	if (!terminationProps || !locationState || !employmentState?.currentEmployee) {
		return <Redirect to={mapHome} />;
	}

	const renderNextSteps = () => {
		if (!isGroupAdmin) {
			return <WysiwygWithReactLink content={pageContent?.nonGroupAdminNextStepsMessage} />;
		}

		return (

			<div className='rich-text'>
				<WysiwygWithReactLink content={pageContent?.groupHealthLifeMessage} />
				{pageContent?.terminateBenefitsLinksText && <Link to={coverageRoute}>{pageContent?.terminateBenefitsLinksText}</Link>}
			</div>

		);
	};

	const successMessage = locationState?.terminationType === TerminationTypes.LeaveOfAbsence ? terminationProps?.loaSubmitSuccessMessage : terminationProps?.terminationSuccessBannerMessage;

	return (
		<PageContainer>
			<MainContent>
				{
					!locationState?.hideSuccess &&
					<ContentSpacer>
						<Banner banner={{
							type: 'success',
							label: <h5>{successMessage || 'Success!'}</h5>,
						}} />
					</ContentSpacer>
				}

				<PageHeader headerText={pageContent?.pageTitle} />
				<DividerSection sectionText={pageContent?.terminatedEmployeeHeader} sectionDescription={pageContent.terminatedEmployeeDescription} removeDivider>

					<h5>{employmentState?.currentEmployee?.full_name}</h5>
					<p>{`${terminationProps?.clientNumberLabel}: ${employmentState?.currentEmployee?.masked_client_number}`}</p>
					{
						pageContent?.loaText &&
						<p>{pageContent?.loaText?.replace('{institution}', locationState?.institution)?.replace('{effective_date}', locationState?.date)?.replace('{name}', employmentState?.currentEmployee?.full_name)}</p>
					}
				</DividerSection>
				{
					(locationState?.terminationType === TerminationTypes.LeaveOfAbsence || locationState?.terminationType === TerminationTypes.Employment) &&
					<DividerSection sectionText={pageContent.benefitTerminatedTitle} removeDivider sectionDescription={pageContent?.benefitTerminatedDescription} />
				}

				<DividerSection sectionText={pageContent.nextStepsHeader} removeDivider>
					{renderNextSteps()}
				</DividerSection>

			</MainContent>

			{/*TODO Updated Mobile  GPS */}
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
}